import React, { useRef } from 'react'
import { motion as Motion, useInView } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../hooks/useAppDispatch.js'
import {
    FaLinkedin,
    FaInstagram,
    FaFacebook,
    FaTwitter,
    FaGithub,
    FaYoutube,
    FaLink
} from 'react-icons/fa'
import { FaXTwitter } from "react-icons/fa6";
import './PublicFooter.scss'

// Assets
import FooterText from '../../assets/Images/Footer/footer-text.png'
import FooterGradient from '../../assets/Images/Footer/footer-gradient.svg'
import QLogo from '../../assets/Images/Footer/q.svg'

const STATIC_SOCIAL_LINKS = [
    { icon: <FaXTwitter size={18} />, url: '#' },
    { icon: <FaFacebook size={18} />, url: '#' },
    { icon: <FaInstagram size={18} />, url: '#' },
    { icon: <FaLinkedin size={18} />, url: '#' },
]

const STATIC_SERVICES_LINKS = [
    { label: 'End-to-End Development', path: '/services/development' },
    { label: 'Solution Discovery', path: '/services/discovery' },
    { label: 'Support & Maintenance', path: '/services/support' },
    { label: 'Tech Consulting', path: '/services/consulting' },
    { label: 'Team Extension', path: '/services/team-extension' },
]

const STATIC_COMPANY_LINKS = [
    { label: 'About Us', path: '/about' },
    { label: 'Contact Us', path: '/contact' },
    { label: 'Blogs', path: '/blogs' },
    { label: 'Careers', path: '/careers' },
    { label: 'Job Openings', path: '/jobs' },
]

export default function PublicFooter() {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { amount: 0.1, once: true })
    const navigate = useNavigate()

    // Get data from Redux store
    const { footer } = useAppSelector((state) => state.global)

    const variants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    }

    // Helper to get social icon
    const getSocialIcon = (platform, iconKey) => {
        const platformLower = platform?.toLowerCase() || iconKey?.toLowerCase() || ''
        const size = 18

        switch (platformLower) {
            case 'twitter': case 'x':
                return <FaXTwitter size={size} />
            case 'linkedin':
                return <FaLinkedin size={size} />
            case 'github':
                return <FaGithub size={size} />
            case 'instagram':
                return <FaInstagram size={size} />
            case 'facebook':
                return <FaFacebook size={size} />
            case 'youtube':
                return <FaYoutube size={size} />
            default:
                return <FaLink size={size} />
        }
    }

    const handleLinkClick = (path) => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        // Navigation is handled by Link natively, but scrolling needs help?
        // Actually Link handles navigation. Sticky headers might need offset.
        // PageFooter used explicit navigate() and window.scrollTo() on a button.
        // We'll attach this to onClick.
    }

    // Determine data sources

    // Social Links
    const socialLinks = footer?.socialIcons?.length > 0
        ? footer.socialIcons.map(s => ({
            icon: getSocialIcon(s.platform, s.iconKey),
            url: s.url
        }))
        : STATIC_SOCIAL_LINKS

    // Services Links
    const servicesColumn = footer?.columns?.find(c => c.title?.toLowerCase() === 'services' || c.title?.toLowerCase().includes('service'))
    const servicesLinks = servicesColumn?.links?.length > 0
        ? servicesColumn.links.map(l => ({ label: l.label, path: l.path }))
        : STATIC_SERVICES_LINKS

    // Company Links
    const companyColumn = footer?.columns?.find(c => c.title?.toLowerCase() === 'company')
    const companyLinks = companyColumn?.links?.length > 0
        ? companyColumn.links.map(l => ({ label: l.label, path: l.path }))
        : STATIC_COMPANY_LINKS

    return (
        <footer className="pf-section" ref={sectionRef}>
            <div className="pf-container pf-relative-z">
                {/* Top Section */}
                <Motion.div
                    className="pf-top"
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    variants={variants}
                >
                    <div className="pf-top-left">
                        <h2>{footer?.heading || "Let’s Build the"}<br />{footer?.subheading ? "" : "Future Together"}</h2>
                        <p className="pf-subtext">{footer?.subheading || "Join the brands building breakthrough platforms with LogiQlink."}</p>
                    </div>

                    <div className="pf-top-right">
                        <Link
                            to="/contact"
                            className="pf-contact-btn"
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        >
                            Contact Us <span className="arrow">→</span>
                        </Link>
                        <span className="pf-or-text">or</span>
                        <div className="pf-call-text" onClick={() => window.location.href = 'tel:+919876543210'}>
                            Call Direct: <span className="pf-number cursor-pointer">+91 98765 43210</span>
                        </div>
                    </div>
                </Motion.div>

                <div className="pf-divider"></div>

                {/* Middle Section */}
                <div className="pf-middle">
                    <div className="pf-brand-col">
                        <div className="pf-logo-wrapper">
                            <img src={QLogo} alt="LogiQlink Logo" className="pf-q-logo" />
                        </div>
                        <p className="pf-brand-desc">
                            {footer?.aboutText || "Lorem ipsum dolor sit amet consectetur. Faucibus vivamus"}
                        </p>
                        <div className="pf-social-row">
                            {socialLinks.map((item, i) => (
                                <a
                                    key={i}
                                    href={item.url}
                                    className="pf-social-icon"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {item.icon}
                                </a>
                            ))}
                        </div>
                    </div>

                    <div className="pf-links-group">
                        <div className="pf-link-col">
                            <h4>Services</h4>
                            <ul>
                                {servicesLinks.slice(0, 4).map((link, i) => (
                                    <li key={i}>
                                        <Link
                                            to={link.path}
                                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="pf-link-col">
                            <h4>Company</h4>
                            <ul>
                                {companyLinks.map((link, i) => (
                                    <li key={i}>
                                        <Link
                                            to={link.path}
                                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Bottom Section */}
                <div className="pf-bottom-bar">
                    <p className="pf-copyright">
                        {footer?.copyrightText || `© ${new Date().getFullYear()} Logiqlink. All rights reserved`}
                    </p>
                    <div className="pf-legal-links">
                        <Link to="/terms" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Terms & conditions</Link>
                        <span className="pf-sep">|</span>
                        <Link to="/privacy" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Privacy Policy</Link>
                        <span className="pf-sep">|</span>
                        <Link to="/cookies" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Cookies Policy</Link>
                    </div>
                </div>
            </div>

            {/* Huge Text Background Effect */}
            <div className="pf-visuals">
                <div className="pf-visual-wrapper">
                    {/* 1. The Huge Text Image (Base) */}
                    <img src={FooterText} alt="LogiQlink" className="pf-visual-text" />

                    {/* 2. The Shadow Overlay (SVG) - Stacked exactly on top */}
                    <img src={FooterGradient} alt="" className="pf-visual-gradient" />
                </div>
            </div>
        </footer>
    )
}